import { Button, Dialog, Stack, TextField, Typography } from "@mui/material";
import { KeyIcon } from "@phosphor-icons/react/dist/csr/Key";
import { MagnifyingGlassIcon } from "@phosphor-icons/react/dist/csr/MagnifyingGlass";
import { VaultIcon } from "@phosphor-icons/react/dist/csr/Vault";

import { ChangeEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CredentialTypes, IfPermission } from "../../../components";
import Permissions from "../../../constants/permissions";
import { useAccountContext } from "../../../strict/app/AccountResolver";
import { useDebounce } from "../../../lib/Hooks";
import { CheckboxFilters } from "../components/CheckboxFilters";
import KeyList from "../components/KeyList";

const STATUS_FILTERS = [
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
];

const KeysPage = () => {
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearchValue = useDebounce(searchValue, 500);

  const [activeStatusFilters, setActiveStatusFilters] = useState<string[]>([]);

  const [showCredentialSelector, setShowCredentialSelector] = useState(false);
  const navigate = useNavigate();
  const { urlName: url_name } = useAccountContext();

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const onCredentialSelected = (value: string) => {
    if (!value) return;
    navigate(`/app/${url_name}/keys/add-key?keyType=${value}`);
  };

  return (
    <>
      <Stack spacing="28px">
        <Stack direction="row" sx={{ justifyContent: "space-between" }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <VaultIcon size={32} />
            <Typography variant="heading.three" sx={{ flex: 2 }}>
              Vault
            </Typography>
          </Stack>
          <Stack direction="row" sx={{ gap: "40px", alignItems: "center", height: "48px" }}>
            <TextField
              sx={{
                backgroundColor: "surfaceLight10.main",
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    border: "none",
                  },
                },
                borderRadius: "4px",
              }}
              placeholder="Search Vault"
              variant="outlined"
              value={searchValue}
              onChange={handleSearch}
              slotProps={{
                input: {
                  endAdornment: <MagnifyingGlassIcon size={20} />,
                  sx: {
                    width: "300px",
                    height: "45px",
                  },
                },
              }}
            />
            <CheckboxFilters
              options={STATUS_FILTERS}
              selectedOptions={activeStatusFilters}
              onClickOption={(value) => {
                setActiveStatusFilters((prev) =>
                  prev.includes(value) ? prev.filter((filter) => filter !== value) : [...prev, value]
                );
              }}
            />
            <IfPermission permission={Permissions.ManageKeys}>
              <Button startIcon={<KeyIcon />} size="large" onClick={() => setShowCredentialSelector(true)}>
                Add key
              </Button>
            </IfPermission>
          </Stack>
        </Stack>
        <KeyList search={debouncedSearchValue} statusFilters={activeStatusFilters} />
      </Stack>
      <Dialog
        open={showCredentialSelector}
        onClose={() => setShowCredentialSelector(false)}
        sx={{
          "& .MuiDialog-container": {
            "& .MuiPaper-root": {
              borderRadius: "4px",
              backgroundColor: "transparent",
              border: "none",
              boxShadow: "none",
              bottom: "10%",
            },
          },
        }}
      >
        <CredentialTypes onChange={onCredentialSelected} />
      </Dialog>
    </>
  );
};

export default KeysPage;
